import React, { useState, useEffect } from 'react';

import {
    CancelPolicyFormWrap,
    FormButtonsWrap,
    ClearButton,
    SubmitButton,
    LoaderIconWrap,
    LoaderIcon,
    WarningText
} from './../cancelPolicy/cancelPolicyForm/cancelPolicyFormStyles';

import { formStatusObj } from '../gpaCancelPolicy/gpaCancelPolicy';

import DownloadDocumentBanner from './../cancelPolicy/cancelPolicyForm/downloadDocument';

import CancellationReason, {
    dropDownInitialState as cancellationReasonDropDownInitialState,
    textBoxInitialState as cancellationReasonTextBoxInitialState,
    textBoxCPPInitialState as cancellationReasonTextBoxCPPInitialState,
    isValid as isCancellationReasonValid,
    reasonOptions as cancellationReasonOptions,
    clReasonCode,
    reasonCodes
} from './../cancelPolicy/cancelPolicyForm/formFields/cancellationReason';

import EffectiveDate, {
    initialState as effectiveDateInitialState,
    isValid as isEffectiveDateValid,
    isEffectiveDateInRange
} from './../cancelPolicy/cancelPolicyForm/formFields/effectiveDate';

import RefundMethod, {
    initialState as refundMethodInitialState,
    isValid as isRefundMethodValid
} from './../cancelPolicy/cancelPolicyForm/formFields/refundMethod';

import CancellationDocument, {
    initialState as cancellationDocumentInitialState,
    isValid as isCancellationDocumentValid
} from './../cancelPolicy/cancelPolicyForm/formFields/cancellationDocument';

import SpinnerSrc from '../../assets/images/spinner-light.svg';
import { simpleGTMDataLayer } from '../../util/GTMHelpers';
import { Warning } from '../../icons';
import generateHolidays from '../../util/holidays/generateHolidays';
import GpaUploadLaterModal from './gpaUploadLaterModal';
import RefundMethods from './RefundMethods';
import { convertToUtc } from '../../util/dateConvertToUTC';

const effectiveDateErrMsg = 'Cannot backdate by more than 2 days';

const initialState = (product: string, system: string) => ({
    cancellationReason:
        system === 'GWCL' &&
            product !== 'COMEMRCIAL PACKAGE' &&
            product !== 'WORKERS COMP'
            ? cancellationReasonTextBoxInitialState
            : cancellationReasonDropDownInitialState,
    cancellationCPPReason: cancellationReasonTextBoxCPPInitialState,
    effectiveDate: effectiveDateInitialState,
    cancellationDocument: cancellationDocumentInitialState,
    refundMethod: refundMethodInitialState
});

interface cancelFormProps {
    submit: Function;
    startCancellation: Function;
    formStatus: string;
    systemCode: string;
    maxDate: string;
    minDate: string;
    cancellationSource: string;
    setCancellationSource: Function;
    product: string;
    initialSource: string;
    close: Function;
    openChanges: boolean;
    getAcceptance: Function;
    acceptable: boolean;
    acceptanceLoading: boolean;
    accountNumber: string;
    policyNumber: string;
    environment: string;
    sourceSystemCode: string;
    getRefundMethodClickHandler: Function;
    refundMethods: string[];
    isFarmRanchLine: boolean;
}

export default ({
    submit,
    startCancellation,
    formStatus,
    systemCode,
    maxDate,
    minDate,
    cancellationSource,
    setCancellationSource,
    product,
    initialSource,
    close,
    openChanges,
    getAcceptance,
    acceptable,
    acceptanceLoading,
    accountNumber,
    policyNumber,
    environment,
    sourceSystemCode,
    getRefundMethodClickHandler,
    refundMethods,
    isFarmRanchLine
}: cancelFormProps) => {
    const [formState, setFormState] = useState(initialState(product, systemCode));
    const [securityType, setSecurityType] = useState<string>('');
    const [reasonCode, setReasonCode] = useState<string>('');
    const [refundMethod, setRefundMethod] = useState<string>('');
    const [refundOptions, setRefundOptions] = useState<string[]>([]);
    const [enableRefundMethod, setEnableRefundMethod] = useState(false);
    const [uploadStatus, setUploadStatus] = useState<string>('upload');
    const [uploadLaterModal, setUploadLaterModal] = useState<boolean>(false);
    const [carrierMovedTo, setCarrierMovedTo] = useState('');
    const [otherCarrier, setOtherCarrier] = useState<string>('');
    const [requestError, setRequestError] = useState({
        requestError: false,
        requestMsg: ''
    });
    const [carrierError, setCarrierError] = useState({
        carrierError: false,
        carrierMsg: ''
    });
    const [otherCarrierError, setOtherError] = useState({
        otherError: false,
        otherMsg: ''
    });
    // const autoRefundMethods = ["Farm Auto Line"];
    const [isAgentSelected, setIsAgentSelected] = useState<boolean>(false);
    const [customTexts, setCustomText] = useState<string>('');

    const holidayList = generateHolidays();
    const clearForm = () => {
        setFormState(initialState(product, systemCode));
        setCancellationSource(initialSource);
        setSecurityType('');
        setCarrierMovedTo('');
        setOtherCarrier('');
        setReasonCode('');
        setRefundMethod('');
        setUploadStatus('upload');
        setRequestError({
            requestError: false,
            requestMsg: ''
        });
        setCarrierError({
            carrierError: false,
            carrierMsg: ''
        });
        setOtherError({
            otherError: false,
            otherMsg: ''
        });
        close();
    };

    const isFormValid = () => {
        let isValid = true;
        const {
            cancellationReason,
            effectiveDate,
            cancellationDocument,
            cancellationCPPReason,
            refundMethod
        } = formState;

        if ((product === 'COMMERCIAL PACKAGE' || product === 'WORKERS COMP' || product === 'General Liability') &&
            (!isCancellationReasonValid(cancellationReason.value) ||
                reasonCode === '')
        ) {
            isValid = false;

            formState.cancellationReason = {
                ...cancellationReason,
                error: true,
                msg: cancellationReasonOptions[0]
            };
        }
        if (((product === 'COMMERCIAL PACKAGE' || product === 'WORKERS COMP' || product === 'General Liability') || isFarmRanchLine) &&
            (!isCancellationReasonValid(cancellationCPPReason.customText) ||
                reasonCode === '')
        ) {
            isValid = false;

            formState.cancellationCPPReason = {
                ...cancellationCPPReason,
                errors: true,
                msgs: 'This is Required.'
            };
        }
        if (
            cancellationSource === 'Select Requested By' &&
            (product === 'WORKERS COMP' || product === 'COMMERCIAL PACKAGE' || product === 'Inland Marine')
        ) {
            isValid = false;
            setRequestError({
                requestError: true,
                requestMsg: 'Select Requested By'
            });
        }
        if (reasonCode === 'movedToOtherCarrier_SAI' && carrierMovedTo === '') {
            isValid = false;
            setCarrierError({
                carrierError: true,
                carrierMsg: 'Select Carrier Moved To'
            });
        }
        if (carrierMovedTo === 'Other' && otherCarrier === '') {
            isValid = false;
            setOtherError({
                otherError: true,
                otherMsg: 'Enter Other Carrier'
            });
        }

        if (!isEffectiveDateValid(effectiveDate.value)) {
            isValid = false;
            const changeObj = {
                error: true,
                msg: 'Invalid date'
            };

            if (
                effectiveDate.value !== '' &&
                !isEffectiveDateInRange(effectiveDate.value, minDate, maxDate)
            ) {
                changeObj.msg = effectiveDateErrMsg;
            }

            formState.effectiveDate = {
                ...effectiveDate,
                ...changeObj
            };
        }
        if (!isRefundMethodValid(refundMethod.value)) {
            isValid = false;
            formState.refundMethod = {
                ...refundMethod,
                error: true,
                msg: 'Please select proper Refund Method'
            };
        }

        if (systemCode === 'GWCL' && !isAgentSelected) {

            if (
                uploadStatus === 'upload' &&
                !isCancellationDocumentValid(
                    uploadStatus,
                    cancellationDocument.file,
                    cancellationDocument.fileName
                )
            ) {
                isValid = false;
                formState.cancellationDocument = {
                    ...cancellationDocument,
                    error: true,
                    msg:
                        'You must upload a signed Cancellation Request/Policy Release (LPR)'
                };
            }

            if (uploadStatus === 'upload' && securityType === '') {
                isValid = false;
                formState.cancellationDocument = {
                    ...cancellationDocument,
                    error: true,
                    msg: 'Select where to send the document'
                };
            }

            if (uploadStatus === 'later') {
                isValid = false;
                formState.cancellationDocument = {
                    ...cancellationDocument,
                    error: true,
                    msg: 'Select later date'
                };
            }
        }

        if (!isValid) {
            setFormState({ ...formState });
        }

        return isValid;
    };

    const returnToEdit = () => {
        setUploadLaterModal(false);
    };

    const uploadAvailable = uploadStatus === 'upload' && systemCode === 'GWCL';
    const defaultCode =
        systemCode === 'GWCL' &&
        product !== 'COMMERCIAL PACKAGE' &&
        product !== 'WORKERS COMP' &&
        product !== 'Inland Marine';
    const [hasBeenCalled, setCalled] = useState<boolean>(false);

    const startCancellationHandler = async () => {
        if (isFormValid()) {
            try {
                startCancellation({
                    cancellationReason: formState.cancellationCPPReason.customText || 'Policy Cancellation',
                    effectiveDate: convertToUtc(formState.effectiveDate.value),
                    cancellationDocument: formState.cancellationDocument.file,
                    securityType,
                    reasonCode: defaultCode ? 'fincononpay' : formState.cancellationReason.value,
                    uploadAvailable,
                    carrierMovedTo,
                    sourceSystemCode,
                    otherCarrier,
                    refundMethod: formState.refundMethod.value,
                });
            } catch (error) {
                console.log('there is some error: ', error);
            }
        }
    };

    const formSubmit = async () => {
        let modelDescription = "Insured's Request";
        if (formState.cancellationReason.value === 'Moved To Other Carrier') {
            modelDescription = 'Moved to Other Carrier';
        } else if (formState.cancellationReason.value === 'Policy Not Taken') {
            modelDescription = formState.cancellationReason.value;
        } else if (
            formState.cancellationReason.value === 'Non-Payment of Premium'
        ) {
            modelDescription = formState.cancellationReason.value;
        }
        const holidayIndicator = holidayList.includes(
            formState.effectiveDate.value
        );
        try {
            await getAcceptance({
                // variables: {
                //   account_number: '100806383C',
                //   policy_number: '10701410FR',
                //   directPCCall: true,
                //   query_env: 'l3-5',
                //   source_system_code: 'GWCL',
                // }

                variables: {
                    account_number: accountNumber,
                    // account_number: '100806383C',
                    policy_number: policyNumber,
                    // policy_number: '10701410FR',
                    // refund_method: 'Pro Rata',
                    // description: modelDescription,
                    // holiday: holidayIndicator,
                    // request_source: cancellationSource,
                    // cancel_date: `${formState.effectiveDate.value}T00:00:00Z`,
                    source_system_code: sourceSystemCode,
                    query_env: environment,
                    directPCCall: false     //TODO: Check for this query variables
                }
            });
            setCalled(true);
        } catch (err) {
            if (err instanceof Error) {
                console.error('Valid Cancel Info Error:', err.message);
            } else {
                console.error('Valid Cancel Info Error:', err);
            }
        }
    };

    const productCPP = product === 'Businessowners Line' || product === 'Commercial Umbrella Line' || product === 'Commercial Auto Line (v7)';

    useEffect(() => {
        if (hasBeenCalled) {
            if (
                formStatus === formStatusObj.notSubmitted
                &&
                isFormValid()
                &&
                !acceptanceLoading
            ) {
                submit({
                    cancellationReason: productCPP ? formState.cancellationCPPReason.customText : formState.cancellationReason.value,
                    effectiveDate: formState.effectiveDate.value,
                    cancellationDocument: formState.cancellationDocument.file,
                    securityType,
                    reasonCode: defaultCode ? 'fincononpay' : reasonCode,
                    uploadAvailable,
                    carrierMovedTo,
                    sourceSystemCode,
                    otherCarrier
                });
            }
        }
    }, [acceptable, acceptanceLoading, hasBeenCalled]);


    useEffect(() => {
        if (product === 'Commercial Property' || product === 'WORKERS COMP' || product === 'COMMERCIAL PACKAGE' || product === 'Inland Marine' || product === 'General Liability') {
            if (!!refundMethods && refundMethods.length > 1)
                formState.refundMethod.value = refundMethods[1];
            if (!!refundMethods && refundMethods.length > 2 && (formState?.cancellationReason?.value === 'fincononpay' || formState?.cancellationReason?.value === 'movedToOtherCarrier_SAI') && refundMethods.includes('prorata')) {
                formState.refundMethod.value = 'prorata';
            }
        }
    }, [refundMethods]);

    useEffect(() => {
        // if (cancellationSource === 'Agent') {
        if (!!isAgentSelected) {
            console.log('Inside ueseffect');
            formState.cancellationCPPReason.customText = customTexts;
        }
    }, [isAgentSelected]);

    const reasonChangeHandler = async (reasonCode: string) => {
        let defaultCode = "fincononpay";
        let cancelReasonCode = "";
        if (sourceSystemCode === "GWPL") {
            cancelReasonCode = reasonCode === "" ? defaultCode : reasonCode;
        } else {
            cancelReasonCode = reasonCode === "" || reasonCode === defaultCode ? defaultCode : reasonCode;
        }
        if (!(product === 'Businessowners Line' || product === 'Commercial Umbrella Line' || product === 'Commercial Auto Line (v7)' || !!isFarmRanchLine)) {

            try {

                await getRefundMethodClickHandler({
                    variables: {
                        policy_number: policyNumber,
                        query_env: environment,
                        source_system_code: sourceSystemCode,
                        getRefundMethods: true,
                        cancellationSource: cancellationSource !== "" ? cancellationSource : 'insured',
                        cancelReasonCode: cancelReasonCode,
                    }
                });

            } catch (err) {
                if (err instanceof Error) {
                    console.error('Error fetching Refund method', err.message);
                } else {
                    console.error('Error fetching Refund method:', err);
                }
            }
        }


    };

    const handleChange = (
        field: string,
        value: string | File | null,
        fileName?: string
    ) => {
        const updateObj = {
            error: false,
            msg: ''
        };
        const updateCPPObj = {
            errors: false,
            msgs: ''
        };

        const {
            cancellationReason,
            effectiveDate,
            cancellationDocument,
            cancellationCPPReason,
            refundMethod
        } = formState;

        if (field === 'cancellationReason' && typeof value === 'string') {

            reasonChangeHandler(value);

            if (product === 'WORKERS COMP' || product === 'COMMERCIAL PACKAGE' || product === 'Inland Marine' || product === 'General Liability') {
                console.log(refundMethods);
            }
            formState.cancellationReason = {
                ...cancellationReason,
                ...updateObj,
                value
            };
        } else if (field === 'effectiveDate' && typeof value === 'string') {
            formState.effectiveDate = {
                ...effectiveDate,
                ...updateObj,
                showDatePicker: false,
                value
            };
        } else if (field === 'refundMethod' && typeof value === 'string') {
            formState.refundMethod = {
                ...refundMethod,
                ...updateObj,
                value
            };
        } else if (
            field === 'cancellationDocument' &&
            typeof value !== 'string' &&
            fileName
        ) {
            formState.cancellationDocument = {
                ...cancellationDocument,
                ...updateObj,
                file: value,
                fileName
            };
        } else if (field === 'cancellationReasonCPP' && typeof value === 'string') {
            formState.cancellationCPPReason = {
                ...cancellationCPPReason,
                ...updateCPPObj,
                customText: value,
            };
        }

        setFormState({ ...formState });
    };

    const handleFocus = (field: string) => {
        const { cancellationReason, cancellationCPPReason, effectiveDate, refundMethod } = formState;

        switch (field) {
            case 'cancellationReason':
                formState.cancellationReason = {
                    ...cancellationReason,
                    active: true
                };
                break;
            case 'cancellationReasonCPP':
                formState.cancellationCPPReason = {
                    ...cancellationCPPReason,
                    isactive: true
                };
                break;
            case 'refundMethod':
                formState.refundMethod = {
                    ...refundMethod,
                    active: true
                };
                break;
            case 'effectiveDate':
            default:
                formState.effectiveDate = {
                    ...effectiveDate,
                    active: true
                };
                break;
        }

        setFormState({ ...formState });
    };

    const handleBlur = (field: string) => {
        const { cancellationReason, cancellationCPPReason, effectiveDate, refundMethod } = formState;

        switch (field) {
            case 'cancellationReason':
                formState.cancellationReason = {
                    ...cancellationReason,
                    active: false
                };
                break;
            case 'refundMethod':
                formState.refundMethod = {
                    ...refundMethod,
                    active: false
                };
                if (formState.refundMethod.value === '') {
                    formState.refundMethod = {
                        ...refundMethod,
                        error: true,
                        active: false
                    };
                }
                break;
            case 'cancellationReasonCPP':
                formState.cancellationCPPReason = {
                    ...cancellationCPPReason,
                    isactive: false
                };
                break;
            case 'effectiveDate':
            default:
                formState.effectiveDate = {
                    ...effectiveDate,
                    active: false
                };
                if (
                    effectiveDate.value !== '' &&
                    !isEffectiveDateInRange(effectiveDate.value, minDate, maxDate)
                ) {
                    formState.effectiveDate = {
                        ...effectiveDate,
                        active: false,
                        error: true,
                        msg: effectiveDateErrMsg
                    };
                }
                break;
        }

        setFormState({ ...formState });
    };

    const toggleCalendar = () => {
        const { effectiveDate } = formState;

        formState.effectiveDate = {
            ...effectiveDate,
            showDatePicker: !effectiveDate.showDatePicker
        };

        setFormState({ ...formState });
    };
    const disabled: boolean = formStatus !== formStatusObj.notSubmitted;

    return (
        <CancelPolicyFormWrap>
            <DownloadDocumentBanner systemCode={systemCode} />
            <CancellationReason
                state={formState.cancellationReason}
                onFocus={() => handleFocus('cancellationReason')}
                onBlur={() => handleBlur('cancellationReason')}
                onChange={(value: string) => handleChange('cancellationReason', value)}
                disabled={disabled}
                reasonCode={reasonCode}
                setReasonCode={setReasonCode}
                cancellationSource={cancellationSource}
                setCancellationSource={setCancellationSource}
                product={product}
                systemCode={systemCode}
                carrierMovedTo={carrierMovedTo}
                setCarrierMovedTo={setCarrierMovedTo}
                otherCarrier={otherCarrier}
                setOtherCarrier={setOtherCarrier}
                requestError={requestError}
                setRequestError={setRequestError}
                carrierError={carrierError}
                setCarrierError={setCarrierError}
                otherCarrierError={otherCarrierError}
                setOtherError={setOtherError}
                cancellationCPPReason={formState.cancellationCPPReason}
                onFocusCPPReason={() => handleFocus('cancellationReasonCPP')}
                onBlurCPPReason={() => handleBlur('cancellationReasonCPP')}
                onChangeCPPReason={(value: string) => handleChange('cancellationReasonCPP', value)}
                onFocusReason={() => handleFocus('cancellationReason')}
                onBlurReason={() => handleBlur('cancellationReason')}
                onChangeReason={(value: string) => handleChange('cancellationReason', value)}
                isFarmRanchLine={isFarmRanchLine}
                isAgentSelected={isAgentSelected}
                setIsAgentSelected={setIsAgentSelected}
                customTexts={customTexts}
                setCustomText={setCustomText}
            />

            <RefundMethod
                state={formState.refundMethod}
                systemCode={systemCode}
                onFocus={() => handleFocus('refundMethod')}
                onBlur={() => handleBlur('refundMethod')}
                onChange={(value: string) => handleChange('refundMethod', value)}
                disabled={refundMethods.length === 1}
                refundOptions={refundMethods}
                setRefundMethod={setRefundMethod}
                product={product}
                cancellationReason={reasonCode}
            />

            <EffectiveDate
                state={formState.effectiveDate}
                toggleCalendar={() => toggleCalendar()}
                onFocus={() => handleFocus('effectiveDate')}
                onBlur={() => handleBlur('effectiveDate')}
                onChange={(value: string) => handleChange('effectiveDate', value)}
                disabled={disabled}
                maximumDate={maxDate}
                minimumDate={minDate}
            />

            <CancellationDocument
                state={formState.cancellationDocument}
                onChange={(file: File, fileName: string) =>
                    handleChange('cancellationDocument', file, fileName)
                }
                disabled={disabled}
                setSecurityType={setSecurityType}
                setUploadStatus={setUploadStatus}
                requestedBy={cancellationSource}
                uploadStatus={uploadStatus}
                securityType={securityType}
                product={sourceSystemCode}
                isFirstPage={true}
            />

            <FormButtonsWrap>
                <ClearButton
                    onClick={() => {
                        simpleGTMDataLayer({
                            event: 'CancelPolicy-ClearButton-click',
                            event_action: 'CancelPolicy ClearButton Click',
                            event_category: 'CancelPolicy',
                            event_label: 'CancelPolicy ClearButton Button Click',
                            event_category_snowplow: 'Snowplow CancelPolicy'
                        });
                        clearForm();
                    }}
                >
                    CLEAR
                </ClearButton>
                <SubmitButton onClick={() => {
                    if (uploadStatus === 'later') {
                        setUploadLaterModal(true);
                    } else {
                        // formSubmit();
                        startCancellationHandler();
                    }
                }}>
                    {formStatus === formStatusObj.notSubmitted ? (
                        'START CANCELLATION'
                    ) : (
                        <>
                            <LoaderIconWrap>
                                <LoaderIcon src={SpinnerSrc} />
                            </LoaderIconWrap>
                            SENDING REQUEST
                        </>
                    )}
                </SubmitButton>
                {uploadLaterModal && <GpaUploadLaterModal showModal={uploadStatus === 'later'} closeModal={returnToEdit} clearForm={clearForm} />}
            </FormButtonsWrap>
        </CancelPolicyFormWrap>
    );
};
